import type { ComponentProps, ReactNode } from "react";

import { useEffect, useRef, useState } from "react";

import {
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Kbd,
  KbdGroup,
  Separator,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@alaffia-technology-solutions/ds";
import {
  useDebounceCallback,
  useKeyPressEvent,
  useResizeObserver,
  useUserAgent,
} from "@alaffia-technology-solutions/hooks";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import type { Icon } from "@tabler/icons-react";

import { IconCards, IconRectangleVertical } from "@tabler/icons-react";
import {
  ChevronLeft,
  ChevronRight,
  CloudDownload,
  Minus,
  Plus,
  RotateCcw,
  RotateCw,
  Search,
  X,
} from "lucide-react";

import type { PDFViewMode, RotateDirection, ZoomStep } from "../helper-types";

interface ToolbarProps {
  zoomScale: number | "fitWidth" | "fitHeight";
  currentPage: number | null;
  pagesCount: number | null;
  viewMode: PDFViewMode | null;
  disabled?: boolean;
  onJumpToPage: (pageIndex: number) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  onZoom: (step: ZoomStep) => void;
  onRotate: (direction: RotateDirection) => void;
  onSearch: (query: string) => void;
  onViewModeChange: (viewMode: PDFViewMode) => void;
  url?: string;
  searchEnabled?: boolean;
  searchTooltip?: ReactNode;
  defaultSearchValue?: string;
}

export const PDFToolbar = ({
  zoomScale,
  pagesCount,
  currentPage,
  viewMode,
  disabled,
  onJumpToPage,
  onNextPage,
  onPrevPage,
  onRotate,
  onZoom,
  onSearch,
  onViewModeChange,
  url,
  searchEnabled = false,
  searchTooltip,
  defaultSearchValue = "",
}: ToolbarProps) => {
  const pageValue = currentPage === 0 || currentPage ? currentPage + 1 : "";
  const [searchInputValue, setSearchInputValue] = useState(defaultSearchValue);
  const [isSearchBarExpanded, setIsSearchBarExpanded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { width: wrapperWidth } = useResizeObserver({ ref: wrapperRef });
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [pageInput, setPageInput] = useState(pageValue);

  const debouncedJumpToPage = useDebounceCallback(onJumpToPage, 500);

  const formattedZoomScale =
    typeof zoomScale === "number"
      ? `${Math.round(zoomScale * 100)}%`
      : zoomScale === "fitWidth"
        ? "Fit Width"
        : "Fit Height";

  const isMobile = Boolean(wrapperWidth && wrapperWidth < 768);

  const handleInputFocus = () => {
    setIsSearchBarExpanded(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      e.relatedTarget !== clearButtonRef.current &&
      e.relatedTarget !== searchButtonRef.current
    ) {
      setIsSearchBarExpanded(false);
    }
  };

  useKeyPressEvent("/", (event) => {
    if (event.ctrlKey || event.metaKey) {
      handleInputFocus();
    }
  });

  useEffect(() => {
    setPageInput(pageValue);
  }, [pageValue]);

  return (
    <div
      className="af-grid af-w-full af-auto-cols-auto af-grid-flow-col af-justify-between af-gap-2 af-overflow-hidden af-overflow-x-auto af-border-b af-border-b-border af-px-1 af-py-2 md:af-items-center"
      ref={wrapperRef}
    >
      <PDFToolbarSearchInput
        value={searchInputValue}
        onValueChange={setSearchInputValue}
        isMobile={isMobile}
        isExpanded={isSearchBarExpanded}
        onIsExpandedChange={setIsSearchBarExpanded}
        onSearch={onSearch}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={!searchEnabled || disabled}
        ref={(el) => {
          if (el && isSearchBarExpanded) el.focus();
        }}
        icon={!searchEnabled && searchTooltip ? searchTooltip : null}
      />

      {(!isMobile || (isMobile && !isSearchBarExpanded)) && (
        <div className="af-flex af-gap-2">
          <Button
            onClick={onPrevPage}
            disabled={disabled}
            variant="ghost"
            size="icon"
          >
            <span className="af-sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <div className="af-flex af-flex-nowrap af-items-center af-gap-2 af-text-nowrap af-text-xs md:af-justify-center md:af-text-sm">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label>
              <span className="af-sr-only">Page number</span>
              <Input
                min={0}
                max={pagesCount ?? 1}
                type="number"
                className="af-hide-arrow-controls af-h-full af-w-11 af-p-1 af-text-center af-text-xs md:af-text-sm"
                value={pageInput}
                onChange={(e) => {
                  debouncedJumpToPage.cancel();
                  const value = parseInt(e.target.value);
                  const clampedValue = Math.max(
                    Math.min(value, pagesCount ?? 1),
                    1,
                  );
                  setPageInput(clampedValue);
                  if (isNaN(value)) {
                    return;
                  }
                  debouncedJumpToPage(clampedValue - 1);
                }}
                disabled={disabled}
              />
            </label>
            <div className={cn("af-min-w-max", disabled && "af-opacity-50")}>
              {" "}
              / {pagesCount}
            </div>
          </div>
          <Button
            onClick={onNextPage}
            disabled={disabled}
            variant="ghost"
            size="icon"
          >
            <span className="af-sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
        </div>
      )}

      <div className="af-flex af-h-full af-w-full af-flex-nowrap af-items-center af-justify-center af-gap-2 af-self-center af-text-xs af-text-foreground md:af-justify-end md:af-text-sm">
        <div className="af-flex af-items-center af-gap-1">
          <span className="af-mr-1 af-text-xs af-font-medium">View</span>
          <PDFToolbarViewModeButton
            buttonViewMode="continuous-view-mode"
            currentViewMode={viewMode}
            onViewModeChange={onViewModeChange}
            disabled={disabled}
          />
          <PDFToolbarViewModeButton
            buttonViewMode="single-page-view-mode"
            currentViewMode={viewMode}
            onViewModeChange={onViewModeChange}
            disabled={disabled}
          />
        </div>
        <Separator orientation="vertical" className="af-h-2/3" />
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRotate("left")}
          disabled={disabled}
        >
          <RotateCcw size={16} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRotate("right")}
          disabled={disabled}
        >
          <RotateCw size={16} />
        </Button>
        {url && (
          <Button size="icon" variant="ghost" disabled={disabled} asChild>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label="Download PDF"
            >
              <CloudDownload className="af-text-foreground" size={16} />
            </a>
          </Button>
        )}
        <Separator orientation="vertical" className="af-h-2/3" />
        <div className="af-flex-no-wrap af-flex af-items-center af-gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onZoom(-0.1)}
            disabled={disabled}
          >
            <Minus size={16} />
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="af-text-nowrap"
                onClick={() => onZoom("fitWidth")}
                aria-label={`Current zoom scale: ${formattedZoomScale}. Click to fit width`}
                disabled={disabled}
              >
                {formattedZoomScale}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="af-text-xs af-text-popover-foreground">
                Click to fit width
              </div>
            </TooltipContent>
          </Tooltip>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onZoom(0.1)}
            disabled={disabled}
          >
            <Plus size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

interface PDFToolbarSearchInputProps extends ComponentProps<typeof Input> {
  isMobile: boolean;
  isExpanded: boolean;
  onIsExpandedChange: (expanded: boolean) => void;
  onSearch: (query: string) => void;
  value: string;
  onValueChange: (value: string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  icon: ReactNode;
}

function PDFToolbarSearchInput({ ref, ...props }: PDFToolbarSearchInputProps) {
  const {
    isMobile,
    isExpanded,
    onIsExpandedChange,
    onSearch,
    value,
    onValueChange,
    onFocus,
    onBlur,
    icon,
    ...restProps
  } = props;
  const userAgent = useUserAgent();
  if (isMobile && !isExpanded) {
    return (
      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={() => {
          onIsExpandedChange(!isExpanded);
        }}
      >
        <span className="af-sr-only">Search</span>
        <Search size={20} />
      </Button>
    );
  }

  return (
    <InputGroup className="af-group/test">
      <InputGroupInput
        className="af-w-full af-min-w-10 !af-outline-none"
        placeholder="Search"
        ref={ref}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyUp={(e) => {
          if (value === "") {
            onSearch("");
          }
          if (e.key === "Enter") {
            onSearch(value);
          }
        }}
        {...restProps}
      />
      <InputGroupAddon align="inline-start">
        <Search className="af-size-5" />
      </InputGroupAddon>
      <InputGroupAddon align="inline-end">{icon}</InputGroupAddon>
      <InputGroupAddon align={"inline-end"}>
        <KbdGroup
          className={
            "group-has-[[data-slot=input-group-control]:focus-visible]/input-group:group-has-[[data-slot=input-group-control]:placeholder-shown]/input-group:af-hidden group-has-[[data-slot=input-group-control]:not(:placeholder-shown)]/input-group:af-hidden"
          }
        >
          <Kbd>{userAgent.os?.name === "macOS" ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>/</Kbd>
        </KbdGroup>

        <InputGroupButton
          className={cn(
            "group-has-[[data-slot=input-group-control]:focus-visible]/input-group:group-has-[[data-slot=input-group-control]:placeholder-shown]/input-group:af-invisible group-has-[[data-slot=input-group-control]:not(:focus-visible)]/input-group:group-has-[[data-slot=input-group-control]:placeholder-shown]/input-group:af-hidden",
          )}
          disabled={props.disabled}
          onClick={() => {
            onSearch("");
            onValueChange("");
            onIsExpandedChange(false);
          }}
        >
          <X className="af-size-4" />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

const viewModeButtons: Record<PDFViewMode, { name: string; icon: Icon }> = {
  "continuous-view-mode": {
    name: "Continuous",
    icon: IconCards,
  },
  "single-page-view-mode": {
    name: "Single Page",
    icon: IconRectangleVertical,
  },
};

interface PDFToolbarViewModeButtonProps {
  currentViewMode: PDFViewMode | null;
  buttonViewMode: PDFViewMode;
  onViewModeChange: (viewMode: PDFViewMode) => void;
  disabled?: boolean;
}

function PDFToolbarViewModeButton({
  currentViewMode,
  buttonViewMode,
  onViewModeChange,
  disabled,
}: PDFToolbarViewModeButtonProps) {
  const { name, icon: Icon } = viewModeButtons[buttonViewMode];
  const title = `Change to ${name} View Mode`;

  return (
    <Tooltip>
      <Toggle
        size="xs"
        pressed={currentViewMode === buttonViewMode}
        onPressedChange={() => onViewModeChange(buttonViewMode)}
        disabled={disabled}
        asChild
      >
        <TooltipTrigger>
          <span className="af-sr-only">{title}</span>
          <Icon className="af-size-4" />
        </TooltipTrigger>
      </Toggle>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}
